<script setup>
import { computed, ref } from 'vue'
import ClassicAuthCard from '../components/ClassicAuthCard.vue'
import { PASSWORD_CONFIRMATION_MISMATCH_MESSAGE } from '../../shared/utils/passwordPolicy.js'

const props = defineProps({
  email: { type: String, default: '' },
  loading: { type: Boolean, default: false },
  passwordError: { type: String, default: '' },
  confirmationError: { type: String, default: '' },
  formError: { type: String, default: '' },
  expiredTokenError: { type: Boolean, default: false },
  isAdmin: { type: Boolean, default: false },
  registerUrl: { type: String, default: '/register' },
})

const emit = defineEmits(['submit', 'clearError', 'request-new-reset'])
const password = ref('')
const passwordConfirmation = ref('')
const confirmationMismatchError = ref('')
const requestResetHref = computed(() => props.isAdmin ? '/admin/login' : '/login')
const displayedConfirmationError = computed(() => confirmationMismatchError.value || props.confirmationError)

function clearFieldError(field) {
  confirmationMismatchError.value = ''
  emit('clearError', field)
}

function submit() {
  if (!password.value || !passwordConfirmation.value || props.loading) return

  confirmationMismatchError.value = ''

  if (password.value !== passwordConfirmation.value) {
    confirmationMismatchError.value = PASSWORD_CONFIRMATION_MISMATCH_MESSAGE
    return
  }

  emit('submit', {
    password: password.value,
    password_confirmation: passwordConfirmation.value,
  })
}
</script>

<template>
  <ClassicAuthCard title="Reset Password" :is-admin="isAdmin" :register-url="registerUrl">
    <form class="classic-set" :class="{ 'classic-set--admin': isAdmin }" @submit.prevent="submit">
      <p v-if="expiredTokenError" class="classic-set__error classic-set__error--form" role="alert">
        This password request link has expired. Please request a new one by clicking this link:
        <a :href="requestResetHref" @click.prevent="emit('request-new-reset')">Request a new password reset link</a>
      </p>
      <p v-else-if="formError" class="classic-set__error classic-set__error--form" role="alert">{{ formError }}</p>

      <label class="classic-set__label" for="classic-set-email">Email *</label>
      <input id="classic-set-email" class="classic-set__input" type="email" :value="email" autocomplete="email" readonly>

      <label class="classic-set__label classic-set__label--spaced" for="classic-set-password">New Password *</label>
      <input
        id="classic-set-password"
        v-model="password"
        class="classic-set__input"
        :class="{ 'classic-set__input--invalid': passwordError }"
        type="password"
        autocomplete="new-password"
        required
        @input="clearFieldError('password')"
      >
      <p v-if="passwordError" class="classic-set__error" role="alert">{{ passwordError }}</p>

      <label class="classic-set__label classic-set__label--spaced" for="classic-set-confirmation">Confirm new Password *</label>
      <input
        id="classic-set-confirmation"
        v-model="passwordConfirmation"
        class="classic-set__input"
        :class="{ 'classic-set__input--invalid': displayedConfirmationError }"
        type="password"
        autocomplete="new-password"
        required
        @input="clearFieldError('password_confirmation')"
      >
      <p v-if="displayedConfirmationError" class="classic-set__error" role="alert">{{ displayedConfirmationError }}</p>

      <button
        class="classic-set__submit"
        type="submit"
        :disabled="loading || !password || !passwordConfirmation"
      >
        {{ loading ? 'Please wait…' : 'Reset Password' }}
      </button>
    </form>
  </ClassicAuthCard>
</template>

<style scoped>
.classic-set { display: flex; flex-direction: column; }
.classic-set__label { color: #333; font-size: 14px; font-weight: 600; margin-bottom: 8px; }
.classic-set__label--spaced { margin-top: 16px; }
.classic-set__input { background: transparent; border: 0; border-bottom: 1px solid #c6c6c6; border-radius: 0; box-sizing: border-box; color: #7c7c7c; font: inherit; min-height: 46px; padding: 10px 0; width: 100%; }
.classic-set__input:focus { border-color: #ff7f5d; box-shadow: none; outline: none; }
.classic-set--admin .classic-set__input:focus { border-color: #56dbcb; }
.classic-set__input--invalid { border-color: #dc3545; }
.classic-set__error { color: #dc3545; font-size: 13px; margin: 7px 0 0; }
.classic-set__error--form { margin: 0 0 18px; }
.classic-set__submit { align-self: center; background: #ff7f5d; border: 0; border-radius: 25px; color: #fff; cursor: pointer; font: inherit; font-weight: 500; margin-top: 28px; min-width: 230px; padding: 12px 20px; }
.classic-set--admin .classic-set__submit { background: #56dbcb; }
.classic-set__submit:disabled { cursor: not-allowed; opacity: 0.6; }
</style>
